import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pagination } from '../../hoc/Pagination'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import { IUser } from '../../types/user'
import { fromTokenToCard } from '../OriginalTokens'
import { IOption, TextButtons } from '../UI/Buttons'
import { Cards } from '../UI/Card/List'
import { auth as authAPI, user as userAPI } from '../../utils/api/index'
import { ICard } from '../../types/card'
import { TRow } from '../../types/api'
import { fromLotsToCard } from '../Lots'
import { EventContext } from '../../ctx/event'
import { Margin } from '../../hoc/Margin'
import { ISelectOption, Select } from '../UI/Select'
import { fromPremiumToCard } from '../Premium'
import { useUserMeta } from '../../hooks/useUserMeta'
import { useDispatch } from 'react-redux'
import { mainSetLoading } from '../../store/actions/main'

export const UserCards = ({ user }: { user: IUser }) => {
    const { auth, main } = useTypedSelector((s) => s)
    const isCreator = useMemo(() => auth.id === user.id, [auth, user])
    const [row, setRow] = useState<TRow<any>>()
    const [cards, setCards] = useState<ICard[]>([])
    const [page, setPage] = useState(1)
    const { hasPremium } = useUserMeta(user)
    const dispatch = useDispatch()

    const categories: IOption[] = useMemo(() => {
        const res: IOption[] = [
            { label: 'NFTs', value: 'Created' },
            { label: 'Owned', value: 'Collected' },
            { label: 'Transactions', value: 'Bids' },
        ]
        if (hasPremium) {
            res.push({
                label: 'Premium',
                value: 'Premium',
                gradient: user.id === auth.id ? false : true,
            })
        }
        return res
    }, [hasPremium, user, auth])
    const sortNFT: ISelectOption[] = useMemo(() => {
        return [
            {
                label: 'Newest',
                value: 'createdAt',
                desc: true,
                sort: 'createdAt',
            },
            {
                label: 'Oldest',
                value: 'createdAtDesc',
                desc: false,
                sort: 'createdAt',
            },
            {
                label: 'By title: A-Z',
                value: 'name',
                desc: false,
                sort: 'name',
            },
            {
                label: 'By title: Z-A',
                value: 'nameDesc',
                desc: true,
                sort: 'name',
            },
        ]
    }, [])
    const sortTransactions: ISelectOption[] = useMemo(() => {
        return [
            {
                label: 'Newest',
                value: 'createdAt',
                desc: true,
                sort: 'createdAt',
            },
            {
                label: 'Oldest',
                value: 'createdAtDesc',
                desc: false,
                sort: 'createdAt',
            },
        ]
    }, [])

    const [category, setCategory] = useState(categories[0])
    const [sortNFTState, setSortNFTState] = useState<ISelectOption>(sortNFT[0])
    const [sortTransactionsState, setSortTransactionsState] =
        useState<ISelectOption>(sortTransactions[0])

    const updateCards = useCallback((data: ICard[], clear?: boolean) => {
        setCards((oldCards) => [...(clear ? [] : oldCards), ...data])
    }, [])

    const getAuthData = useCallback(
        (page: number, clear?: boolean) => {
            const params = {
                page,
                sortBy: sortNFTState.sort as any,
                sortDesc: sortNFTState.desc as boolean,
            }
            const paramsBids = {
                page,
                sortBy: sortTransactionsState.sort as any,
                sortDesc: sortTransactionsState.desc as boolean,
            }
            switch (category.value) {
                case 'Created':
                    return authAPI.lotsCreated(params).then((res) => {
                        setRow(res.data)
                        updateCards(fromTokenToCard(res.data.rows), clear)
                    })
                case 'Collected':
                    return authAPI.lotsCollected(params).then((res) => {
                        setRow(res.data)
                        updateCards(fromTokenToCard(res.data.rows), clear)
                    })
                case 'Bids':
                    return authAPI.lotsBets(paramsBids).then((res) => {
                        setRow(res.data)
                        updateCards(fromLotsToCard(res.data.rows), clear)
                    })
                case 'Premium':
                    return authAPI.premium
                        .list({ ...paramsBids, userId: user.id })
                        .then((res) => {
                            setRow(res.data)
                            updateCards(fromPremiumToCard(res.data.rows), clear)
                        })
                default:
                    return
            }
        },
        [category, sortNFTState, sortTransactionsState, user, updateCards]
    )
    const getUserData = useCallback(
        (page: number, clear?: boolean) => {
            const params = {
                page,
                sortBy: sortNFTState.sort as any,
                sortDesc: sortNFTState.desc as boolean,
            }
            const paramsBids = {
                page,
                sortBy: sortTransactionsState.sort as any,
                sortDesc: sortTransactionsState.desc as boolean,
            }
            switch (category.value) {
                case 'Created':
                    return userAPI.tokenOriginal
                        .created(user.id, params)
                        .then((res) => {
                            setRow(res.data)
                            updateCards(fromTokenToCard(res.data.rows), clear)
                        })
                case 'Collected':
                    return userAPI.tokenOriginal
                        .collected(user.id, params)
                        .then((res) => {
                            updateCards(fromTokenToCard(res.data.rows), clear)
                            setRow(res.data)
                        })
                case 'Bids':
                    return userAPI.tokenOriginal
                        .bets(user.id, paramsBids)
                        .then((res) => {
                            setRow(res.data)
                            updateCards(fromLotsToCard(res.data.rows), clear)
                        })
                case 'Premium':
                    return authAPI.premium
                        .list({ ...paramsBids, userId: user.id })
                        .then((res) => {
                            setRow(res.data)
                            updateCards(fromPremiumToCard(res.data.rows), clear)
                        })
                default:
                    return
            }
        },
        [category, sortNFTState, sortTransactionsState, user, updateCards]
    )

    function addData() {
        setPage(page + 1)
        isCreator ? getAuthData(page + 1) : getUserData(page + 1)
    }

    const getDataFromStart = useCallback(async () => {
        dispatch(mainSetLoading(true))
        setPage(1)
        setRow(undefined)
        try {
            isCreator ? await getAuthData(1, true) : await getUserData(1, true)
        } catch (e) {}
        dispatch(mainSetLoading(false))
    }, [getAuthData, getUserData, isCreator, dispatch])

    function removeCard(id: string) {
        setCards(cards.filter((t) => t.id !== id))
    }

    useEffect(() => {
        getDataFromStart()
    }, [getDataFromStart])

    return (
        <EventContext.Provider
            value={{
                onPaginationPage: addData,
                onCardRemove: removeCard,
                onCardHided: removeCard,
            }}
        >
            <TextButtons
                options={categories}
                state={[category, setCategory]}
            ></TextButtons>
            <Margin margin={{ top: 20 }}>
                <div className="selects">
                    <div className="selects__item">
                        {category.value === 'Bids' ? (
                            <Select
                                title="Filter:"
                                preset="select-text"
                                options={sortTransactions}
                                state={[
                                    sortTransactionsState,
                                    setSortTransactionsState,
                                ]}
                            ></Select>
                        ) : (
                            <Select
                                title="Filter:"
                                preset="select-text"
                                options={sortNFT}
                                state={[sortNFTState, setSortNFTState]}
                            ></Select>
                        )}
                    </div>
                </div>
            </Margin>

            {row ? (
                <Margin margin={{ top: 20 }}>
                    <Pagination row={row} length={cards.length}>
                        {!main.loading && cards.length ? (
                            <Cards cards={cards}></Cards>
                        ) : (
                            <div className="empty">Nothing here yet</div>
                        )}
                    </Pagination>
                </Margin>
            ) : null}
        </EventContext.Provider>
    )
}
